import type { DateRange } from "@prismicio/editor-support/Date"
import { Box, Text } from "@prismicio/editor-ui"
import { type PropsWithChildren, type ReactNode, Suspense } from "react"

import { UsageChartCard, UsageChartCardSkeleton } from "./UsageChartCard"
import { UsageDetailsCard, UsageDetailsCardSkeleton } from "./UsageDetailsCard"
import { UsageProgressBar, UsageProgressBarSkeleton } from "./UsageProgressBar"
import {
  UsageSummaryItemDescription,
  UsageSummaryItemDescriptionSkeleton,
} from "./UsageSummaryItemDescription"
import { usageSummaryItemTypes } from "./usageSummaryItemTypes"

interface UsageSummaryItemProps extends UsageSummaryItemSkeletonProps {
  selectedDateRange: DateRange
  planLimitsKnown: boolean
}

export function UsageSummaryItem(props: UsageSummaryItemProps) {
  const { itemTypeKey, selectedDateRange, planLimitsKnown, ...otherProps } =
    props
  return (
    <UsageSummaryItemTemplate
      {...otherProps}
      title={usageSummaryItemTypes[itemTypeKey].name}
      description={
        <Suspense
          fallback={
            <UsageSummaryItemDescriptionSkeleton
              planLimitsKnown={planLimitsKnown}
            />
          }
        >
          <UsageSummaryItemDescription
            itemTypeKey={itemTypeKey}
            planLimitsKnown={planLimitsKnown}
          />
        </Suspense>
      }
    >
      <Suspense
        fallback={
          <UsageProgressBarSkeleton planLimitsKnown={planLimitsKnown} />
        }
      >
        <UsageProgressBar
          itemTypeKey={itemTypeKey}
          selectedDateRange={selectedDateRange}
          planLimitsKnown={planLimitsKnown}
        />
      </Suspense>
      <Suspense
        fallback={
          <UsageDetailsCardSkeleton planLimitsKnown={planLimitsKnown} />
        }
      >
        <UsageDetailsCard
          itemTypeKey={itemTypeKey}
          selectedDateRange={selectedDateRange}
          planLimitsKnown={planLimitsKnown}
        />
      </Suspense>
      <Suspense fallback={<UsageChartCardSkeleton />}>
        <UsageChartCard
          itemTypeKey={itemTypeKey}
          selectedDateRange={selectedDateRange}
        />
      </Suspense>
    </UsageSummaryItemTemplate>
  )
}

type UsageSummaryItemTemplateProps = PropsWithChildren<{
  title: ReactNode
  description: ReactNode
  billingPeriodPicker?: ReactNode
}>

function UsageSummaryItemTemplate(props: UsageSummaryItemTemplateProps) {
  const { title, description, billingPeriodPicker, children } = props
  return (
    <Box flexDirection="column" gap={16}>
      <Box gap={8}>
        <Box flexDirection="column" flexGrow={1} gap={4}>
          <Text component="h4" variant="bold">
            {title}
          </Text>
          {description}
        </Box>
        {billingPeriodPicker}
      </Box>
      {children}
    </Box>
  )
}

export interface UsageSummaryItemSkeletonProps
  extends Pick<UsageSummaryItemTemplateProps, "billingPeriodPicker"> {
  itemTypeKey: keyof typeof usageSummaryItemTypes
}

export function UsageSummaryItemSkeleton(props: UsageSummaryItemSkeletonProps) {
  const { itemTypeKey, ...otherProps } = props
  return (
    <UsageSummaryItemTemplate
      {...otherProps}
      title={usageSummaryItemTypes[itemTypeKey].name}
      description={<UsageSummaryItemDescriptionSkeleton />}
    >
      <UsageProgressBarSkeleton />
      <UsageDetailsCardSkeleton />
      <UsageChartCardSkeleton />
    </UsageSummaryItemTemplate>
  )
}
